import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const TreeContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  padding: 2rem 0;
`;

const TreeWrapper = styled.div`
  min-width: 1000px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Generation = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    width: 2px;
    height: 20px;
    background-color: #ddd;
    display: ${props => props.isLast ? 'none' : 'block'};
  }
`;

const PersonCard = styled(motion.div)`
  background: white;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  padding: 1rem;
  margin: 0 1rem;
  width: 180px;
  text-align: center;
  cursor: pointer;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    width: 2px;
    height: 20px;
    background-color: #ddd;
    display: ${props => props.hasChildren ? 'block' : 'none'};
  }
  
  &:before {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 25%;
    width: 50%;
    height: 2px;
    background-color: #ddd;
    display: ${props => props.hasChildren ? 'block' : 'none'};
  }
  
  .person-image {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    margin: 0 auto 1rem;
    background-color: #f0f0f0;
    background-size: cover;
    background-position: center;
    border: 3px solid ${props => props.gender === 'male' ? '#6c5ce7' : '#e84393'};
  }
  
  h4 {
    margin-bottom: 0.5rem;
    color: #333;
  }
  
  .person-years {
    font-size: 0.9rem;
    color: #666;
    margin-bottom: 0.5rem;
  }
`;

const PersonDetails = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 2rem;
`;

const PersonDetailsContent = styled.div`
  background: white;
  border-radius: 10px;
  max-width: 500px;
  width: 100%;
  padding: 2rem;
  position: relative;
  
  .close-button {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
  }
  
  .person-header {
    display: flex;
    align-items: center;
    margin-bottom: 1.5rem;
    
    .person-image {
      width: 100px;
      height: 100px;
      border-radius: 50%;
      margin-right: 1.5rem;
      background-color: #f0f0f0;
      background-size: cover;
      background-position: center;
      border: 3px solid ${props => props.gender === 'male' ? '#6c5ce7' : '#e84393'};
    }
    
    .person-info {
      h3 {
        margin-bottom: 0.5rem;
        color: #333;
      }
      
      .person-years {
        font-size: 1rem;
        color: #666;
      }
    }
  }
  
  .person-bio {
    margin-bottom: 1.5rem;
    
    h4 {
      color: #6c5ce7;
      margin-bottom: 0.5rem;
    }
    
    p {
      color: #666;
      line-height: 1.6;
    }
  }
  
  .person-facts {
    h4 {
      color: #6c5ce7;
      margin-bottom: 0.5rem;
    }
    
    ul {
      padding-left: 1.5rem;
      
      li {
        margin-bottom: 0.5rem;
        color: #666;
      }
    }
  }
`;

const FamilyTree = () => {
    const [selectedPerson, setSelectedPerson] = useState(null);

    // Sample family tree data
    const familyData = {
        generation1: [
            {
                id: 1,
                name: 'John Dumani',
                years: '1920-2005',
                gender: 'male',
                image: 'https://source.unsplash.com/random/200x200/?oldman',
                bio: 'The patriarch of the Dumani family, John immigrated to present day Zimbabwe in 1940 and established the family\'s first business.',
                facts: [
                    'Served in World War II',
                    'Founded Dumani Hardware in 1950',
                    'Father of 4 children',
                    'Community leader and philanthropist'
                ],
                hasChildren: true
            },
            {
                id: 2,
                name: 'Mary Dumani',
                years: '1925-2010',
                gender: 'female',
                image: 'https://source.unsplash.com/random/200x200/?oldwoman',
                bio: 'Mary was the heart of the Dumani family, known for her warmth, wisdom, and delicious family recipes.',
                facts: [
                    'Immigrated from Ireland in 1945',
                    'Renowned for her apple pie recipe',
                    'Organized the first family reunion in 1970',
                    'Volunteer at local hospital for 40 years'
                ],
                hasChildren: true
            }
        ],
        generation2: [
            {
                id: 3,
                name: 'Robert Dumani',
                years: '1950-',
                gender: 'male',
                image: 'https://source.unsplash.com/random/200x200/?man',
                bio: 'The eldest son of John and Mary, Robert expanded the family business into construction.',
                facts: [
                    'Founded Dumani & Sons Construction in 1978',
                    'Married to Sarah for 45 years',
                    'Father of 2 children',
                    'Avid golfer and community leader'
                ],
                hasChildren: true
            },
            {
                id: 4,
                name: 'James Dumani',
                years: '1953-',
                gender: 'male',
                image: 'https://source.unsplash.com/random/200x200/?man2',
                bio: 'James pursued academia and became a respected professor of history.',
                facts: [
                    'PhD in American History',
                    'Published author of 3 books',
                    'Father of 3 children',
                    'Family historian'
                ],
                hasChildren: true
            },
            {
                id: 5,
                name: 'Elizabeth Mlalazi',
                years: '1955-',
                gender: 'female',
                image: 'https://source.unsplash.com/random/200x200/?woman',
                bio: 'Elizabeth became a renowned physician and humanitarian.',
                facts: [
                    'Medical doctor specializing in pediatrics',
                    'Volunteer work in developing countries',
                    'Mother of 2 children',
                    'Established family health foundation'
                ],
                hasChildren: true
            },
            {
                id: 6,
                name: 'William Dumani',
                years: '1960-2015',
                gender: 'male',
                image: 'https://source.unsplash.com/random/200x200/?man3',
                bio: 'The youngest child of John and Mary, William was an artist and musician.',
                facts: [
                    'Renowned painter and jazz musician',
                    'Traveled extensively throughout South Africa',
                    'Father of 1 child',
                    'Art displayed in several galleries'
                ],
                hasChildren: true
            }
        ],
        generation3: [
            {
                id: 7,
                name: 'Michael Dumani',
                years: '1980-',
                gender: 'male',
                image: 'https://source.unsplash.com/random/200x200/?youngman',
                bio: 'Michael followed in his father\'s footsteps and now runs the family farm.',
                facts: [
                    'Organic farming pioneer',
                    'Married to Emily',
                    'Father of 2 children',
                    'Advocate for sustainable agriculture'
                ],
                hasChildren: false
            },
            {
                id: 8,
                name: 'Jennifer Dumani',
                years: '1982-',
                gender: 'female',
                image: 'https://source.unsplash.com/random/200x200/?youngwoman',
                bio: 'Jennifer is a tech entrepreneur and founder of Dumani Tech Solutions.',
                facts: [
                    'Computer Science degree from MIT',
                    'Founded tech company in 2010',
                    'Mentor for women in STEM',
                    'Frequent speaker at technology conferences'
                ],
                hasChildren: false
            },
            {
                id: 9,
                name: 'David Dumani',
                years: '1985-',
                gender: 'male',
                image: 'https://source.unsplash.com/random/200x200/?youngman2',
                bio: 'David combined family tradition with innovation by opening Heritage Bakery.',
                facts: [
                    'Trained chef in Paris',
                    'Married to Emily',
                    'Father of 1 child',
                    'Preserves family recipes with modern twists'
                ],
                hasChildren: false
            },
            {
                id: 10,
                name: 'Thomas Dumani',
                years: '1975-',
                gender: 'male',
                image: 'https://source.unsplash.com/random/200x200/?lawyer',
                bio: 'Thomas is an attorney who provides legal counsel to all family businesses.',
                facts: [
                    'Law degree from Harvard',
                    'Founded Dumani Legal Partners',
                    'Family business advisor',
                    'Pro bono work for community organizations'
                ],
                hasChildren: false
            },
            {
                id: 11,
                name: 'Jessica Dumani',
                years: '1990-',
                gender: 'female',
                image: 'https://source.unsplash.com/random/200x200/?youngwoman2',
                bio: 'Jessica is an environmental scientist working on climate research.',
                facts: [
                    'PhD in Environmental Science',
                    'Research published in leading journals',
                    'Passionate about conservation',
                    'Helps family businesses adopt sustainable practices'
                ],
                hasChildren: false
            }
        ]
    };

    return (
        <TreeContainer>
            <TreeWrapper>
                <Generation>
                    {familyData.generation1.map(person => (
                        <PersonCard
                            key={person.id}
                            gender={person.gender}
                            hasChildren={person.hasChildren}
                            onClick={() => setSelectedPerson(person)}
                            whileHover={{ y: -5 }}
                        >
                            <div className="person-image" style={{ backgroundImage: `url(${person.image})` }}></div>
                            <h4>{person.name}</h4>
                            <div className="person-years">{person.years}</div>
                        </PersonCard>
                    ))}
                </Generation>

                <Generation>
                    {familyData.generation2.map(person => (
                        <PersonCard
                            key={person.id}
                            gender={person.gender}
                            hasChildren={person.hasChildren}
                            onClick={() => setSelectedPerson(person)}
                            whileHover={{ y: -5 }}
                        >
                            <div className="person-image" style={{ backgroundImage: `url(${person.image})` }}></div>
                            <h4>{person.name}</h4>
                            <div className="person-years">{person.years}</div>
                        </PersonCard>
                    ))}
                </Generation>

                <Generation isLast>
                    {familyData.generation3.map(person => (
                        <PersonCard
                            key={person.id}
                            gender={person.gender}
                            hasChildren={person.hasChildren}
                            onClick={() => setSelectedPerson(person)}
                            whileHover={{ y: -5 }}
                        >
                            <div className="person-image" style={{ backgroundImage: `url(${person.image})` }}></div>
                            <h4>{person.name}</h4>
                            <div className="person-years">{person.years}</div>
                        </PersonCard>
                    ))}
                </Generation>
            </TreeWrapper>

            {selectedPerson && (
                <PersonDetails
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setSelectedPerson(null)}
                >
                    <PersonDetailsContent
                        gender={selectedPerson.gender}
                        onClick={e => e.stopPropagation()}
                    >
                        <button className="close-button" onClick={() => setSelectedPerson(null)}>✕</button>
                        <div className="person-header">
                            <div className="person-image" style={{ backgroundImage: `url(${selectedPerson.image})` }}></div>
                            <div className="person-info">
                                <h3>{selectedPerson.name}</h3>
                                <div className="person-years">{selectedPerson.years}</div>
                            </div>
                        </div>
                        <div className="person-bio">
                            <h4>Biography</h4>
                            <p>{selectedPerson.bio}</p>
                        </div>
                        <div className="person-facts">
                            <h4>Notable Facts</h4>
                            <ul>
                                {selectedPerson.facts.map((fact, index) => (
                                    <li key={index}>{fact}</li>
                                ))}
                            </ul>
                        </div>
                    </PersonDetailsContent>
                </PersonDetails>
            )}
        </TreeContainer>
    );
};

export default FamilyTree;